import { type FindCaseRecordsRequest, SearchField } from '@compass/client/types/case_record';
import { Sort } from '@compass/client/types/common';
import { EmptyState } from '@components/EmptyState';
import { GenericTable } from '@components/GenericTable';
import {
  getFieldNameFromPath,
  getSearchFieldEnum,
  isAdvancedSearchField,
  useCasesByAdvancedSearch,
  useCasesByWorkflow,
} from '@hooks/useCases';
import { useOrgAndPrograms } from '@hooks/useOrgAndPrograms';
import { queryKeys } from '@services/query.config';
import { useStore } from '@store/index';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CircleCheck, UserCircle, X, XCircle } from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { AssigneeConfirmationDialog } from '@/components/AssigneeConfirmationDialog';
import {
  AssigneeDropdown,
  type AssigneeOption,
  type AssigneeOptionGroup,
} from '@/components/AssigneeDropdown';
import { BulkAssigneeDropdown } from '@/components/BulkAssigneeDropdown';
import { BulkStatusUpdateModal } from '@/components/BulkStatusUpdateModal';
import { DownloadDropdown } from '@/components/DownloadDropdown';
import { ErrorContainer } from '@/components/ErrorContainer';
import { Breadcrumb, BreadcrumbPortal, HeaderActionsPortal } from '@/components/Header';
import { QuickFilters } from '@/components/QuickFilters';
import { StatusConfirmationDialog } from '@/components/StatusConfirmationDialog';
import { createCasesTableConfig } from '@/components/TableBuilder';
import { Button } from '@/components/ui/button';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import { SearchInput } from '@/components/ui/search-input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { StaticMultiSelect } from '@/components/ui/static-trigger-multi-select';
import { DISPOSITION_WORKFLOW_TYPES, WorkflowType } from '@/constants/dispositions';
import { PAGE_SIZE_OPTIONS } from '@/constants/pagination';
import { useActiveFiltersConfig } from '@/hooks/useActiveFiltersConfig';
import { filterAssigneeGroups, useAssigneeOptions } from '@/hooks/useAssigneeOptions';
import { useBelowProgramAdminUsers } from '@/hooks/useBelowProgramAdminUsers';
import { useBulkSelection } from '@/hooks/useBulkSelection';
import { useColumnCustomization } from '@/hooks/useColumnCustomization';
import { useCursorPagination } from '@/hooks/useCursorPagination';
import { useDispositions } from '@/hooks/useDispositions';
import { useDownloadCaseRecords } from '@/hooks/useDownloadCaseRecords';
import { useDownloadSOB } from '@/hooks/useDownloadSOB';
import { useFilterParams } from '@/hooks/useFilterParams';
import { useUsersByIds } from '@/hooks/useUsers';
import { BulkCreateCasesModal } from '@/pages/campaigns/CreateCampaign';
import {
  type CaseTypeFilter,
  getPqcPinnedDateRange,
  useCasesFilters,
} from '@/pages/cases/hooks/useCasesFilters';
import { programService } from '@/services/api';
import { isProgramSOBEnabled } from '@/services/sobService';
import { isHumanAuditForProgramAdmins } from '@/utils/caseActions';
import { partitionCaseIdSearch, splitSearchValue } from '@/utils/caseSearch';
import { dateToNaiveString, naiveDateToUtc, naiveStringToDate } from '@/utils/date';
import { getErrorButtonConfig } from '@/utils/errorButtonConfig';
import { PA_STATUS_COLUMN_LEAF_IDS } from '@/utils/paStatusColumns';

// Helper function to build search filters for query params
const buildSearchFilters = (searchType: string, searchValue: string, caseTypeFilter?: string) => {
  const fieldName = getFieldNameFromPath(searchType);
  const searchFieldEnum = getSearchFieldEnum(fieldName);

  // Determine which search field to use:
  // 1. If text search is active, use that
  // 2. If case type filter is active (and not "all"), use that
  const hasCaseTypeFilter = caseTypeFilter && caseTypeFilter !== 'all';
  const hasTextSearch = searchFieldEnum && searchValue;

  // Text search takes priority over case type filter
  const effectiveSearchField = hasTextSearch
    ? searchFieldEnum
    : hasCaseTypeFilter
      ? SearchField.SEARCH_FIELD_CASE_TYPE
      : undefined;
  const effectiveSearchValue = hasTextSearch
    ? searchValue
    : hasCaseTypeFilter
      ? caseTypeFilter
      : undefined;

  // When searching by case_id, separate ObjectIDs from case codes:
  // ObjectIDs go to case_record_ids, everything else goes to case_codes.
  const { caseRecordIds, caseCodes } =
    searchType === 'case_id' && searchValue
      ? partitionCaseIdSearch(searchValue)
      : { caseRecordIds: undefined, caseCodes: undefined };

  return {
    reference_ids:
      searchType === 'reference_id' && searchValue ? splitSearchValue(searchValue) : undefined,
    case_record_ids: caseRecordIds,
    case_codes: caseCodes,
    payor_name:
      searchType === 'input_data.data.payor_name' && searchValue ? searchValue : undefined,
    // Atlas Search params for advanced field filtering (used by counts API)
    search_field: effectiveSearchField,
    search_value: effectiveSearchValue,
  };
};

const CasesContainer: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [isBulkCreateModalOpen, setIsBulkCreateModalOpen] = useState(false);
  const { downloadCaseRecords } = useDownloadCaseRecords();
  const { downloadBulkSOB, isDownloading: isDownloadingSOB } = useDownloadSOB();

  // Handle org/program URL sync
  const { selectedOrganizationId, selectedProgramId, unauthorizedAccess } = useOrgAndPrograms();

  const {
    selectedWorkflowId,
    setSelectedWorkflowId,
    getWorkflowsArray,
    workflows,
    testModeEnabled,
    casesPageSize,
    setCasesPageSize,
    casesSelectedIds,
    setCasesSelectedIds,
    clearCasesSelectedIds,
    getSelectedWorkflow,
    getTimeZone,
  } = useStore();

  const workflowsArray = getWorkflowsArray();

  // Get current timezone for date conversions
  const timeZone = getTimeZone();

  // Get selected workflow early to determine default date range
  const selectedWorkflow = getSelectedWorkflow();

  // Use the custom hook for column configuration (early to get allowed field paths)
  const { columnConfig, selectedFields, handleFieldSelectionChange } = useColumnCustomization();

  // Build allowed field paths from column configuration for URL validation
  const allowedFieldPaths = useMemo(() => columnConfig.map(col => col.fieldPath), [columnConfig]);

  // Initialize filter params hook for the initial workflow-id read. URL writes
  // are owned by `useCasesFilters` (write-through setters) — the previous
  // `syncFiltersToUrl` useEffect in this file raced with the user's
  // `navigate(...)` and has been moved out.
  const { getInitialValues, searchParams } = useFilterParams();
  const initialWorkflowId = getInitialValues().workflow;

  // Track if we've initialized from URL (prevents race condition on workflow switch)
  const hasInitializedFromUrl = useRef(false);

  // Track if URL contains invalid workflow ID
  const [invalidWorkflowId, setInvalidWorkflowId] = useState<string | null>(null);

  // Initialize workflow from URL if valid (runs ONCE on mount when workflows load)
  useEffect(() => {
    // Only run once - prevents reverting when user switches workflow
    if (hasInitializedFromUrl.current) {
      return;
    }

    if (initialWorkflowId && workflowsArray.length > 0) {
      const isValidWorkflow = workflowsArray.some(w => w.id === initialWorkflowId);
      if (isValidWorkflow) {
        setSelectedWorkflowId(initialWorkflowId);
        hasInitializedFromUrl.current = true;
        setInvalidWorkflowId(null);
      } else {
        // Mark as invalid - don't auto-select, show error instead
        setInvalidWorkflowId(initialWorkflowId);
        hasInitializedFromUrl.current = true;
      }
    }
  }, [initialWorkflowId, workflowsArray, setSelectedWorkflowId]);

  // Get the appropriate filter config based on workflow type using the hook
  const activeFiltersConfig = useActiveFiltersConfig();

  // pageSize is stored in Zustand for persistence across navigation
  const pageSize = casesPageSize;
  const setPageSize = setCasesPageSize;

  // All filter state + URL sync + workflow-switch reset + cache save live in
  // `useCasesFilters`. The hook returns write-through setters that write URL
  // synchronously inside their respective event handlers — replacing the
  // previous URL-sync useEffect that raced with `navigate(...)` calls under
  // React Router 7's startTransition wrapping of location state.
  const {
    activeQuickFilter,
    searchValues,
    activeSearchType,
    debouncedSearchValue,
    assignedFilter,
    dateFrom,
    dateTo,
    sortState,
    caseTypeFilter,
    setActiveQuickFilter,
    setSearchValues,
    setActiveSearchType,
    setAssignedFilter,
    setDateRange,
    setSortState,
    setCaseTypeFilter,
  } = useCasesFilters({
    selectedWorkflow: selectedWorkflow ?? undefined,
    selectedWorkflowId: selectedWorkflowId ?? undefined,
    selectedProgramId: selectedProgramId ?? undefined,
    workflows,
    activeFiltersConfig,
    allowedFieldPaths,
    timeZone,
  });

  // Use cursor pagination hook with reset dependencies
  const { currentPage, handlePageChange, getCurrentCursor } = useCursorPagination({
    pageType: 'cases',
    resetDependencies: [
      activeQuickFilter,
      activeSearchType,
      debouncedSearchValue, // Reset pagination when search value changes
      selectedWorkflowId,
      assignedFilter,
      dateFrom,
      dateTo,
      pageSize,
      sortState,
      caseTypeFilter,
    ],
    onReset: clearCasesSelectedIds, // Clear selections when pagination resets
  });

  // Get the active filter configuration by id
  const activeTabConfig = activeFiltersConfig.filters.find(f => f.id === activeQuickFilter);
  const activeFilterConfig = activeTabConfig?.filter || {};

  // Highlight case ID from navigation state (after creating a new case)
  const highlightCaseId = location.state?.highlightCaseId || null;

  // Clear highlight from navigation state after it's been used
  useEffect(() => {
    if (highlightCaseId) {
      const timer = setTimeout(() => {
        navigate(location.pathname, { replace: true, state: {} });
      }, 3500);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [highlightCaseId, navigate, location.pathname]);

  // Fetch program data to get available users
  // Inline useProgram query since the hook was merged
  const { data: programData } = useQuery({
    queryKey: ['program', selectedProgramId],
    queryFn: () => {
      const { selectedOrganizationId } = useStore.getState();
      return programService.getProgram({
        organization_id: selectedOrganizationId!,
        program_id: selectedProgramId!,
      });
    },
    enabled: !!selectedProgramId,
  });

  // Team-grouped assignee options for bulk assign dropdown (filtered by workflow type)
  const { groups: assigneeGroups } = useAssigneeOptions(selectedWorkflow?.workflow_type);

  // All program members for the case list assignee filter (not filtered by workflow type,
  // since the list may contain cases across different workflow types)
  const { groups: allProgramGroups, usersMap: allProgramUsersMap } = useAssigneeOptions();

  // Team-grouped filter options. A leading no-heading group holds "Unassigned", then one
  // group per team. Option values stay emails (''=unassigned), so the assigned_user_ids
  // query is unchanged — the grouping is purely presentational.
  // Note: a user on multiple teams appears under each team heading; toggling one toggles the
  // others too, since selection is keyed by email.
  const assigneeFilterOptions = useMemo<AssigneeOptionGroup[]>(() => {
    const groups: AssigneeOptionGroup[] = [
      {
        id: '__unassigned__',
        label: '',
        options: [{ id: '__unassigned__', email: '', name: 'Unassigned' }],
      },
    ];
    for (const group of allProgramGroups) {
      groups.push({
        id: group.teamId,
        label: group.teamName,
        options: group.members.map<AssigneeOption>(member => ({
          id: member.userId,
          name: member.name || undefined,
          email: member.email,
          available: allProgramUsersMap.get(member.userId)?.available ?? false,
        })),
      });
    }
    return groups;
  }, [allProgramGroups, allProgramUsersMap]);

  // Refetch user availability when assignee filter opens
  const handleAssigneeFilterOpen = React.useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['users', 'byIds'] });
  }, [queryClient]);

  // When searching by case_id, remove other filters to get exact match
  const isSearchingByCaseId = activeSearchType === 'case_id' && debouncedSearchValue;

  // Shared filter logic used by both list API (queryParams) and counts API (QuickFilters baseFilters)
  // This ensures consistent filtering behavior between the case list and quick filter counts
  const sharedSearchFilters = useMemo(
    () => ({
      ...buildSearchFilters(activeSearchType, debouncedSearchValue, caseTypeFilter),
      // When searching by case_id, skip assigned and date filters to get exact match
      assigned_user_ids: isSearchingByCaseId
        ? undefined
        : assignedFilter.length > 0
          ? assignedFilter
          : undefined,
      // Convert naive dates (YYYY-MM-DD) to UTC based on user's timezone
      created_at_after: isSearchingByCaseId
        ? undefined
        : dateFrom
          ? naiveDateToUtc(dateFrom, timeZone, 'start')
          : undefined,
      created_at_before: isSearchingByCaseId
        ? undefined
        : dateTo
          ? naiveDateToUtc(dateTo, timeZone, 'end')
          : undefined,
    }),
    [
      activeSearchType,
      debouncedSearchValue,
      caseTypeFilter,
      isSearchingByCaseId,
      assignedFilter,
      dateFrom,
      dateTo,
      timeZone,
    ],
  );

  // Tabs with `hidesDatePicker` (PQC Pending/Submitted) ignore the picker and
  // pin list + count to the last 12 months (AQ-863) so the query stays bounded.
  // The picker's range still drives the other tabs' counts.
  const pinnedDates = useMemo(() => {
    if (isSearchingByCaseId) return undefined;
    const range = getPqcPinnedDateRange(timeZone);
    return {
      created_at_after: naiveDateToUtc(range.from, timeZone, 'start'),
      created_at_before: naiveDateToUtc(range.to, timeZone, 'end'),
    };
  }, [isSearchingByCaseId, timeZone]);
  const hidesDatePicker = !!activeTabConfig?.hidesDatePicker;
  const listSearchFilters = useMemo(
    () => (hidesDatePicker ? { ...sharedSearchFilters, ...pinnedDates } : sharedSearchFilters),
    [hidesDatePicker, sharedSearchFilters, pinnedDates],
  );

  // Get sort direction for a field
  const getSortDirection = (field: string): 'asc' | 'desc' | undefined => {
    const sort = sortState.find(s => s.field === field || s.field.includes(field));
    return sort?.direction;
  };

  // Check if current search type uses advanced search (Atlas Search)
  // Also use advanced search when case type filter is active
  // Note: When searching by case_id, we use standard search and ignore case_type filter
  const hasCaseTypeFilter = caseTypeFilter && caseTypeFilter !== 'all' && !isSearchingByCaseId;
  const hasAdvancedTextSearch = isAdvancedSearchField(activeSearchType) && debouncedSearchValue;
  // PQC submitted-substate tabs carry output_conditions → served by the Atlas
  // advanced path. Skipped when a text search is active in the box (a single
  // $search can't combine input-text search with output conditions).
  const useTabOutputConditions =
    !isSearchingByCaseId && !hasAdvancedTextSearch && !!activeFilterConfig.outputConditions?.length;
  const isAdvancedSearch = hasAdvancedTextSearch || hasCaseTypeFilter || useTabOutputConditions;

  // Standard-path filter: drop output_conditions (not a FindCaseRecords field).
  const { outputConditions: _outputConditions, ...standardFilter } = activeFilterConfig;
  void _outputConditions;

  // Build the query parameters for reuse
  const queryParams: FindCaseRecordsRequest = {
    workflow_id: selectedWorkflowId || '',
    // When searching by case_id, skip status/quick filters
    ...(isSearchingByCaseId ? {} : standardFilter),
    ...listSearchFilters,
    limit: pageSize,
    // On output-condition tabs the cursor is a keyset page_token (used by the
    // advanced path), not an _id — don't feed it to the standard query.
    start_after_id: useTabOutputConditions ? undefined : getCurrentCursor(),
    sort_by_created_at: getSortDirection(
      'created_at',
    ) as FindCaseRecordsRequest['sort_by_created_at'],
    sort_by_payor_name: getSortDirection(
      'payor_name',
    ) as FindCaseRecordsRequest['sort_by_payor_name'],
  };

  // Fetch cases using standard search (for non-advanced search fields)
  const standardSearchResult = useCasesByWorkflow(queryParams);

  // Fetch cases using advanced search (Atlas Search for dynamic fields)
  // Text search is the primary search, case_type_filter is combined with AND logic
  // Uses sharedSearchFilters for date/assigned filters to ensure consistency
  const advancedSearchResult = useCasesByAdvancedSearch({
    field: getFieldNameFromPath(activeSearchType),
    value: debouncedSearchValue,
    workflow_id: selectedWorkflowId || undefined,
    limit: pageSize,
    created_at_after: listSearchFilters.created_at_after,
    created_at_before: listSearchFilters.created_at_before,
    assigned_user_ids: listSearchFilters.assigned_user_ids,
    case_type_filter: caseTypeFilter,
    // Forward the active quick-filter's case states so the state tabs (AI Call,
    // Human Call, Human Audit, …) also narrow results when an Atlas text search
    // (e.g. first name) is active, AND so output-condition (PQC lifecycle) tabs
    // scope to their states. Skipped for case_id search, which ignores tabs.
    states: isSearchingByCaseId ? undefined : activeFilterConfig.states,
    // PQC lifecycle-tab params (output-condition tabs only)
    output_conditions: useTabOutputConditions ? activeFilterConfig.outputConditions : undefined,
    sort_field: useTabOutputConditions ? activeTabConfig?.advancedSort : undefined,
    sort_order:
      useTabOutputConditions && activeTabConfig?.advancedSort
        ? activeTabConfig.advancedSortAsc
          ? Sort.SORT_ASC
          : Sort.SORT_DESC
        : undefined,
    page_token: useTabOutputConditions ? getCurrentCursor() : undefined,
  });

  // Use the appropriate result based on search type
  const {
    isLoading: casesLoading,
    error,
    cases,
    data,
  } = isAdvancedSearch ? advancedSearchResult : standardSearchResult;

  // Sync advanced search results to Zustand store
  const { setCases } = useStore();
  useEffect(() => {
    if (isAdvancedSearch && cases.length > 0) {
      setCases(cases);
    }
  }, [isAdvancedSearch, cases, setCases]);

  // Store the last case ID of current page for next page navigation.
  // Output-condition tabs use the server's keyset page_token as the cursor instead.
  const lastCaseId = cases.length > 0 ? cases[cases.length - 1]?.id : undefined;
  const nextPageCursor = useTabOutputConditions ? advancedSearchResult.nextCursor : lastCaseId;

  const casesError = error?.message || null;
  // Use the has_more field from backend response
  const hasNextPage = data?.has_more || false;

  // Bulk selection state and handlers - must be after cases and selectedWorkflow are defined
  const {
    selectedIds,
    setSelectedIds,
    handleSelectAllCurrentPage,
    handleBulkStatusUpdate,
    handleBulkAssigneeSelect,
    canBulkAssign,
    selectedCases,
    bulkStatusUpdateModal,
    handleCancelBulkStatusUpdate,
    bulkAssigneeUpdateModal,
    handleConfirmBulkAssigneeUpdate,
    handleCancelBulkAssigneeUpdate,
    // Status confirmation dialog handlers
    statusConfirmationDialog,
    handleIndividualCaseAction,
    showStatusConfirmation,
    handleStatusConfirmationClose,
    handleStatusConfirmationConfirm,
  } = useBulkSelection({
    cases,
    selectedIds: casesSelectedIds,
    setSelectedIds: setCasesSelectedIds,
  });

  // A transportation or unifiedmedbv case in human_audit can be assigned only to a Program
  // Admin or a higher role (BV-2657). When the selection holds one, bulk assign offers only
  // users who are not below Program Admin on its program.
  const reservedSelectionPrograms = useMemo(
    () => [
      ...new Set(
        selectedCases
          .filter(c => isHumanAuditForProgramAdmins(c.state, c.workflow_type))
          .map(c => c.program_id)
          .filter((id): id is string => !!id),
      ),
    ],
    [selectedCases],
  );
  const bulkAssigneeUserIds = useMemo(
    () => [...new Set(assigneeGroups.flatMap(group => group.members.map(m => m.userId)))],
    [assigneeGroups],
  );
  const { isBelow: isBelowProgramAdmin } = useBelowProgramAdminUsers({
    organizationId: selectedOrganizationId ?? undefined,
    programIds: reservedSelectionPrograms,
    userIds: bulkAssigneeUserIds,
    enabled: reservedSelectionPrograms.length > 0,
  });
  const bulkAssigneeGroups = useMemo(
    () =>
      reservedSelectionPrograms.length > 0
        ? filterAssigneeGroups(
            assigneeGroups,
            userId => !reservedSelectionPrograms.some(p => isBelowProgramAdmin(p, userId)),
          ).groups
        : assigneeGroups,
    [assigneeGroups, reservedSelectionPrograms, isBelowProgramAdmin],
  );

  // Fetch dispositions only for disposition-supporting workflows
  const isDispositionWorkflow = DISPOSITION_WORKFLOW_TYPES.includes(
    selectedWorkflow?.workflow_type ?? '',
  );
  const { data: dispositions = [] } = useDispositions(
    isDispositionWorkflow ? selectedWorkflow?.workflow_type : undefined,
  );
  const dispositionsMap = useMemo(
    () => new Map(dispositions.map(d => [d.code!, d.label!] as const)),
    [dispositions],
  );

  // Collect created_by user IDs for user lookup
  const createdByUserIds = useMemo(
    () => cases.map(c => c.created_by).filter((id): id is string => !!id),
    [cases],
  );
  const { usersMap, isLoading: usersLoading } = useUsersByIds(createdByUserIds);

  // Create table configuration with dynamic columns
  const tableConfig = useMemo(
    () =>
      createCasesTableConfig(
        selectedWorkflow,
        columnConfig,
        handleIndividualCaseAction,
        (assigneeUserId, caseId, teamId) =>
          handleBulkAssigneeSelect(assigneeUserId, new Set([caseId]), teamId),
        undefined, // getRowLink
        undefined, // getRowLinkState
        usersMap,
        usersLoading,
        undefined, // excludeColumns
        dispositionsMap,
        { activeTabId: activeQuickFilter, includeColumns: ['total_call_attempts'] }, // hardcoded per-tab PQC columns
      ),
    [
      selectedWorkflow,
      columnConfig,
      handleIndividualCaseAction,
      handleBulkAssigneeSelect,
      usersMap,
      usersLoading,
      dispositionsMap,
      activeQuickFilter,
    ],
  );

  // Show loading state based on:
  // 1. Organization/Program data is still loading
  // 2. Workflows are still loading
  // 3. Cases are loading (but only if we have a workflow selected)
  const isLoadingState = !!selectedWorkflowId && casesLoading;

  // Render breadcrumb and actions early
  const breadcrumb = (
    <BreadcrumbPortal>
      <Breadcrumb>
        <Breadcrumb.Item>Cases</Breadcrumb.Item>
      </Breadcrumb>
    </BreadcrumbPortal>
  );

  const headerActions = (
    <HeaderActionsPortal>
      <div className="flex items-center gap-3">
        {!testModeEnabled && (
          <>
            <DownloadDropdown
              onDownload={async (format, callsOption, includeEligibilityChecks) => {
                const {
                  limit: _limit,
                  start_after_id: _startAfterId,
                  ...downloadFilters
                } = queryParams;
                void _limit;
                void _startAfterId;
                await downloadCaseRecords(
                  selectedProgramId || '',
                  {
                    ...downloadFilters,
                    ...(selectedIds.size > 0 && { case_record_ids: Array.from(selectedIds) }),
                  },
                  format,
                  includeEligibilityChecks || false,
                  undefined,
                  callsOption || 'none',
                  selectedWorkflow?.workflow_type,
                );
              }}
              disabled={!selectedProgramId || !selectedWorkflowId || cases.length === 0}
              workflowType={selectedWorkflow?.workflow_type}
              onlyShowIcon={true}
              showSOBOption={selectedProgramId ? isProgramSOBEnabled(selectedProgramId) : false}
              onDownloadSOB={async () => {
                if (selectedOrganizationId && selectedProgramId && programData?.program?.name) {
                  await downloadBulkSOB(
                    Array.from(selectedIds),
                    selectedOrganizationId,
                    selectedProgramId,
                    programData.program.name,
                  );
                }
              }}
              isDownloadingSOB={isDownloadingSOB}
              sobLabel={
                selectedIds.size > 0 ? `Download SOB (${selectedIds.size})` : 'Download SOB'
              }
            />
          </>
        )}
        <Button
          data-analytics-id="case:create"
          onClick={() =>
            navigate(
              `../create-case${selectedWorkflowId ? `?workflow_id=${selectedWorkflowId}` : ''}`,
            )
          }
          size="sm"
        >
          Create Case
        </Button>
      </div>
    </HeaderActionsPortal>
  );

  // Show unauthorized access message if trying to access a case from an org the user doesn't belong to
  if (unauthorizedAccess?.isUnauthorized) {
    return (
      <>
        {breadcrumb}
        {headerActions}
        <EmptyState
          title="Access Denied"
          description="You do not have access to this organization. Please contact your administrator if you believe this is an error."
          icon={XCircle}
          iconClassName="text-muted-foreground"
          action={{
            label: 'Go to Home',
            onClick: () => navigate('/'),
          }}
        />
      </>
    );
  }

  // Show error state for invalid workflow ID
  if (invalidWorkflowId) {
    return (
      <>
        {breadcrumb}
        {headerActions}
        <EmptyState
          title="Workflow Not Found"
          description={`The workflow "${invalidWorkflowId}" does not exist or you don't have access to it.`}
          icon={XCircle}
          iconClassName="text-muted-foreground"
          action={{
            label: 'View All Cases',
            onClick: () => {
              setInvalidWorkflowId(null);
              // Clear workflow_id from URL
              const params = new URLSearchParams(searchParams);
              params.delete('workflow_id');
              navigate(`?${params.toString()}`, { replace: true });
            },
          }}
        />
      </>
    );
  }

  if (error) {
    const buttonConfig = getErrorButtonConfig(error?.APIHttpStatus, navigate);
    return (
      <>
        {breadcrumb}
        {headerActions}
        <ErrorContainer
          errorMessage={error?.userMessage}
          buttonText={buttonConfig?.text}
          buttonOnClick={buttonConfig?.onClick}
        />
      </>
    );
  }

  return (
    <>
      {breadcrumb}
      {headerActions}

      <div className="flex flex-col h-full">
        {/* Main Content */}
        <div className="flex-1 px-6 overflow-hidden">
          <div className="flex flex-col h-full min-h-0">
            {/* Search Box and Filters */}
            <div className="flex justify-between items-center mt-4">
              <div className="flex flex-wrap items-center gap-3">
                {/* Search Input */}
                <SearchInput
                  searchOptions={[
                    { key: 'case_id', label: 'Case ID' },
                    // Only include reference_id if it exists in table columns
                    ...(tableConfig.columns.some(col => col.key === 'referenceId')
                      ? [{ key: 'reference_id', label: 'Reference ID' }]
                      : []),
                    // Only include fields that are selected AND support Atlas Search.
                    // The PA Status leaves are excluded: the workflow-gated block below
                    // offers them unconditionally, so without this guard a selected PA
                    // column would list the same field twice.
                    ...columnConfig
                      .filter(
                        col =>
                          selectedFields.includes(col.id) &&
                          isAdvancedSearchField(col.fieldPath) &&
                          !PA_STATUS_COLUMN_LEAF_IDS.has(getFieldNameFromPath(col.fieldPath)),
                      )
                      .map(col => ({
                        key: col.fieldPath,
                        label: `${col.label} ${col.group ? `(${col.group})` : ''}`,
                      })),
                    // Referral Scheduler and Recall Scheduler shared fields
                    ...(['referral_scheduler', 'recall_scheduler'].includes(
                      selectedWorkflow?.workflow_type ?? '',
                    )
                      ? [
                          { key: 'phone_mobile', label: 'Mobile Phone Number' },
                          { key: 'phone_home', label: 'Home Phone Number' },
                          {
                            key: 'reachout_eligibility_status',
                            label: 'Reachout Eligibility Status',
                          },
                        ]
                      : []),
                    // Referral Scheduler only fields
                    ...(selectedWorkflow?.workflow_type === 'referral_scheduler'
                      ? [
                          { key: 'ineligibility_tag', label: 'Ineligibility Tag' },
                          { key: 'medical_record_number', label: 'Medical Record Number' },
                        ]
                      : []),
                    // PA Status workflows — always show the output-data filters,
                    // regardless of which columns the user has selected, so a field can be
                    // filtered on without first adding its column. These five now DO have
                    // columns (`applyPaStatusOutputFields`), which is why the intersection
                    // above suppresses them: this block is their single source here.
                    // The server resolves each leaf to the flat pbm path plus both medbv
                    // per-code paths and ORs them.
                    ...((
                      [WorkflowType.PA_STATUS_PBM, WorkflowType.PA_STATUS_MEDBV] as string[]
                    ).includes(selectedWorkflow?.workflow_type ?? '')
                      ? [
                          { key: 'prior_authorization_on_file_status', label: 'PA Status' },
                          { key: 'prior_authorization_required', label: 'PA Required' },
                          { key: 'prior_authorization_on_file_exists', label: 'PA on File Exists' },
                          { key: 'appeal_available', label: 'Appeal Available' },
                          { key: 'appeal_status', label: 'Appeal Status' },
                        ]
                      : []),
                    // Transportation workflow — always show these in the search dropdown
                    // regardless of which columns the user has selected.
                    // Note: transportation uses "payer_name" (with E), PharmBV uses "payor_name" (with O).
                    ...(selectedWorkflow?.workflow_type === 'transportation'
                      ? [
                          { key: 'first_name', label: 'Patient First Name' },
                          { key: 'last_name', label: 'Last Name' },
                          { key: 'payer_name', label: 'Insurance Company Name' },
                          { key: 'member_id', label: 'Member ID' },
                          { key: 'phone', label: 'Payer Phone' },
                        ]
                      : []),
                  ]}
                  activeSearchType={activeSearchType}
                  searchValues={searchValues}
                  onSearchValueChange={(type, value) => {
                    setSearchValues(prev => ({ ...prev, [type]: value }));
                  }}
                  onSearchTypeChange={newType => {
                    setActiveSearchType(newType);
                  }}
                />
                <AssigneeDropdown
                  mode="multi"
                  options={assigneeFilterOptions}
                  selected={assignedFilter}
                  onChange={setAssignedFilter}
                  placeholder="Select assignee"
                  className="min-w-[200px] max-w-[300px] px-3"
                  disabled={!selectedProgramId}
                  triggerIcon={UserCircle}
                  onOpen={handleAssigneeFilterOpen}
                  searchPlaceholder="Search assignees..."
                />
                {/* Date Range Filter — hidden on tabs pinned to the default range
                 * (PQC Pending/Submitted, AQ-863; see hidesDatePicker). */}
                {!hidesDatePicker && (
                  <DateRangePicker
                    initialDateFrom={dateFrom ? naiveStringToDate(dateFrom) : undefined}
                    initialDateTo={dateTo ? naiveStringToDate(dateTo) : undefined}
                    timezone={timeZone}
                    onUpdate={({ range }) => {
                      // If no `from` is selected, keep the current date range.
                      if (!range.from) return;
                      // Convert Date objects to naive date strings (YYYY-MM-DD).
                      // Timezone conversion happens at API request time. If
                      // only `from` is set, mirror it onto `to` (single-day
                      // selection).
                      setDateRange(
                        dateToNaiveString(range.from),
                        dateToNaiveString(range.to ?? range.from),
                      );
                    }}
                    showCompare={false}
                  />
                )}
                {/* Case Type Filter - Only for PQC workflow */}
                {selectedWorkflow?.workflow_type === 'pqc' && (
                  <Select
                    value={caseTypeFilter}
                    onValueChange={v => setCaseTypeFilter(v as CaseTypeFilter)}
                  >
                    <SelectTrigger className="w-[160px] shrink-0">
                      <SelectValue placeholder="Case Type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      <SelectItem value="AE">AE</SelectItem>
                      <SelectItem value="PQC">PQC</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>

            {/* Quick Filters */}
            <div className="mt-4 flex justify-between items-center">
              <QuickFilters
                activeFilter={activeQuickFilter}
                onFilterChange={setActiveQuickFilter}
                workflowId={selectedWorkflowId || ''}
                filtersConfig={activeFiltersConfig}
                baseFilters={sharedSearchFilters}
                pinnedDates={pinnedDates}
              />
              {cases.length > 0 && (
                <div className="flex items-center gap-2">
                  <StaticMultiSelect
                    options={columnConfig.map(col => ({
                      label: col.label,
                      value: col.id,
                      group: col.group,
                    }))}
                    selected={selectedFields}
                    onChange={handleFieldSelectionChange}
                    placeholder="Customize columns"
                    showClearAll={false}
                    size="sm"
                  />
                </div>
              )}
            </div>

            {/* Cases Table */}
            <GenericTable
              config={tableConfig}
              data={cases}
              loading={isLoadingState}
              error={casesError}
              currentPage={currentPage}
              hasNextPage={hasNextPage}
              onPageChange={newPage => handlePageChange(newPage, nextPageCursor)}
              highlightId={highlightCaseId}
              searchTerm={debouncedSearchValue}
              filters={{ status: activeQuickFilter, assigned: assignedFilter }}
              pageSize={pageSize}
              pageSizeOptions={[...PAGE_SIZE_OPTIONS]}
              onPageSizeChange={setPageSize}
              selectionMode={true}
              selectedIds={selectedIds}
              onSelectionChange={setSelectedIds}
              onSelectAllCurrentPage={handleSelectAllCurrentPage}
              // The Atlas advanced path sorts by a fixed sort_field and ignores
              // per-column sortState — disable + clear column-header sort there so
              // clicking a header isn't a no-op that only resets paging / shows a
              // stale arrow. Standard tabs keep full column sort.
              sortState={isAdvancedSearch ? [] : sortState}
              onSortChange={isAdvancedSearch ? undefined : setSortState}
            />
          </div>
        </div>

        {/* Bulk Create Cases Modal */}
        <BulkCreateCasesModal
          open={isBulkCreateModalOpen}
          onOpenChange={setIsBulkCreateModalOpen}
          onSuccess={() => {
            // Invalidate cases queries to refresh the data without full page reload
            queryClient.invalidateQueries({ queryKey: queryKeys.cases.all });
            // Also invalidate campaigns if they exist
            queryClient.invalidateQueries({ queryKey: queryKeys.campaigns.all });
            // Close the modal
            setIsBulkCreateModalOpen(false);
          }}
        />

        {/* Floating Bulk Selection Controls */}
        {selectedIds.size > 0 && (
          <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50">
            <div className="bg-foreground text-background rounded-full shadow-lg px-6 py-3 flex items-center gap-6">
              <span className="text-sm font-medium whitespace-nowrap">
                {selectedIds.size} case{selectedIds.size === 1 ? '' : 's'} selected
              </span>

              <div className="w-px h-4 bg-background/20 shrink-0"></div>

              <BulkAssigneeDropdown
                selectedCount={selectedIds.size}
                canBulkAssign={canBulkAssign}
                assigneeGroups={bulkAssigneeGroups}
                onAssigneeSelect={(email, teamId) =>
                  handleBulkAssigneeSelect(email, undefined, teamId)
                }
              />

              <div className="w-px h-4 bg-background/20 shrink-0"></div>

              <Button
                variant="ghost"
                size="sm"
                onClick={handleBulkStatusUpdate}
                className="h-auto p-0 bg-transparent hover:bg-transparent text-background hover:text-background/70 text-sm font-medium flex items-center gap-1 whitespace-nowrap shrink-0 transition-opacity"
              >
                <CircleCheck className="w-4 h-4 shrink-0" />
                <span>Change Status</span>
              </Button>

              <div className="w-px h-4 bg-background/20 shrink-0"></div>

              <DownloadDropdown
                onDownload={async (format, callsOption, includeEligibilityChecks) => {
                  const {
                    limit: _limit2,
                    start_after_id: _startAfterId2,
                    ...downloadFilters
                  } = queryParams;
                  void _limit2;
                  void _startAfterId2;
                  await downloadCaseRecords(
                    selectedProgramId || '',
                    {
                      ...downloadFilters,
                      ...(selectedIds.size > 0 && { case_record_ids: Array.from(selectedIds) }),
                    },
                    format,
                    includeEligibilityChecks || false,
                    undefined,
                    callsOption || 'none',
                    selectedWorkflow?.workflow_type,
                  );
                }}
                disabled={selectedIds.size === 0}
                buttonClassName="h-auto p-0 bg-transparent hover:bg-transparent text-background hover:text-background/70 text-sm font-medium flex items-center gap-1 whitespace-nowrap shrink-0 transition-opacity"
                variant="floating"
                hideChevron
                workflowType={selectedWorkflow?.workflow_type}
                showSOBOption={selectedProgramId ? isProgramSOBEnabled(selectedProgramId) : false}
                onDownloadSOB={async () => {
                  if (selectedOrganizationId && selectedProgramId && programData?.program?.name) {
                    await downloadBulkSOB(
                      Array.from(selectedIds),
                      selectedOrganizationId,
                      selectedProgramId,
                      programData.program.name,
                    );
                  }
                }}
                isDownloadingSOB={isDownloadingSOB}
                sobLabel={
                  selectedIds.size > 0 ? `Download SOB (${selectedIds.size})` : 'Download SOB'
                }
              />

              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedIds(new Set())}
                className="text-background hover:bg-transparent hover:text-background p-1 h-6 w-6 rounded-full shrink-0"
              >
                <X className="w-3 h-3" />
              </Button>
            </div>
          </div>
        )}

        {/* Bulk Status Update Modal */}
        <BulkStatusUpdateModal
          isOpen={bulkStatusUpdateModal.isOpen}
          onClose={handleCancelBulkStatusUpdate}
          selectedCases={bulkStatusUpdateModal.selectedCases}
          workflow={selectedWorkflow}
          onConfirm={showStatusConfirmation}
        />

        {/* Bulk Status Confirmation Dialog */}
        <StatusConfirmationDialog
          isOpen={statusConfirmationDialog.isOpen}
          onClose={handleStatusConfirmationClose}
          onConfirm={handleStatusConfirmationConfirm}
          statusUpdatePlans={statusConfirmationDialog.statusUpdatePlans}
          workflow={selectedWorkflow}
          cases={cases}
          isIndividual={statusConfirmationDialog.isIndividual}
        />

        {/* Bulk Assignee Confirmation Dialog */}
        <AssigneeConfirmationDialog
          isOpen={bulkAssigneeUpdateModal.isOpen}
          onClose={handleCancelBulkAssigneeUpdate}
          onConfirm={handleConfirmBulkAssigneeUpdate}
          assigneeUpdatePlan={bulkAssigneeUpdateModal.assigneeUpdatePlan}
          cases={cases}
          isIndividual={bulkAssigneeUpdateModal.assigneeUpdatePlan?.count === 1}
        />
      </div>
    </>
  );
};

export default CasesContainer;
